import React from 'react';
import { useTranslation } from 'react-i18next';
import { Moon, RefreshCw, Settings, Sun } from 'lucide-react';
import { AppContext } from '../context/AppContext';

export const ThemeToggle: React.FC<{
  isDarkMode: boolean;
  setIsDarkMode: (val: boolean) => void;
  className?: string;
  /** Wrap the small visual mark in a 44×44 transparent hit area (iOS HIG). */
  enlargeHitArea?: boolean;
}> = ({ isDarkMode, setIsDarkMode, className = '', enlargeHitArea = false }) => {
  const { t } = useTranslation();
  const buttonClass = enlargeHitArea
    ? `group inline-flex h-11 min-h-[44px] w-11 min-w-[44px] shrink-0 items-center justify-center rounded-full bg-transparent text-dg-fg-primary transition-colors hover:bg-dg-interaction-hover/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dg-primary-600 dark:text-slate-100 dark:hover:bg-white/5`
    : `inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-dg-control border border-dg-border bg-dg-bg-surface/90 text-dg-fg-primary shadow-dg-card transition-colors hover:bg-dg-interaction-hover dark:border-slate-600 dark:bg-slate-900/90 dark:text-slate-100 dark:hover:bg-slate-800 ${className}`;
  const visualMark = (
    <span
      aria-hidden
      className={
        enlargeHitArea
          ? `inline-flex h-4 w-4 items-center justify-center rounded-full shadow-sm transition-colors ${className}`
          : ''
      }
    >
      {isDarkMode ? (
        <Sun className="h-3 w-3" aria-hidden />
      ) : (
        <Moon className="h-3 w-3" aria-hidden />
      )}
    </span>
  );
  return (
    <button
      onClick={() => setIsDarkMode(!isDarkMode)}
      title={t('pageWrapper.toggleTheme')}
      aria-label={t('pageWrapper.toggleTheme')}
      type="button"
      className={buttonClass}
    >
      {visualMark}
    </button>
  );
};

/**
 * Header sync icon button.
 *
 * - Stays pinned dead-centre via the absolute wrapper (same slot the
 *   legacy `NetworkStatusDot` lived in).
 * - Renders a single circular Lucide `RefreshCw` icon button. The
 *   icon's color communicates state:
 *     * Offline → muted amber, button disabled.
 *     * Online + idle/ok → emerald, button enabled, click triggers
 *       the supplied sync handler.
 *     * Online + sync error → rose, button enabled, click retries.
 *     * `isSyncing` → icon spins via `animate-spin`, `aria-busy`,
 *       button disabled to prevent stacking sync calls.
 * - The wording is carried by `aria-label` + `title` (no persistent
 *   text label sits next to the icon).
 * - When neither `onClick` nor a fallback is provided the button
 *   still renders but is disabled (`aria-disabled="true"`,
 *   `cursor-not-allowed`) so the dead-center anchor never disappears.
 */
const HeaderSyncButton: React.FC<{
  isOnline: boolean;
  syncStatus: 'idle' | 'ok' | 'error';
  onClick?: () => void;
  isSyncing?: boolean;
}> = ({ isOnline, syncStatus, onClick, isSyncing = false }) => {
  const { t } = useTranslation();

  const offlineLabel = t('pageWrapper.titleOffline');
  const syncErrorLabel = t('pageWrapper.titleSyncError');
  const syncNowLabel = t('pageWrapper.syncNow', { defaultValue: 'Sync now' });
  const retryLabel = t('pageWrapper.retryClassroomSync');

  const ariaLabel = !isOnline
    ? t('pageWrapper.statusOffline')
    : syncStatus === 'error'
      ? retryLabel
      : syncNowLabel;

  const titleText = !isOnline
    ? offlineLabel
    : syncStatus === 'error'
      ? syncErrorLabel
      : syncNowLabel;

  const iconColor = !isOnline
    ? 'text-amber-500 dark:text-amber-400'
    : syncStatus === 'error'
      ? 'text-rose-500 dark:text-rose-400'
      : 'text-emerald-500 dark:text-emerald-400';

  const handlerWired = typeof onClick === 'function';
  const disabled = !isOnline || isSyncing || !handlerWired;

  return (
    <div
      className="pointer-events-none absolute inset-y-0 left-1/2 z-10 flex -translate-x-1/2 items-center"
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={handlerWired ? onClick : undefined}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-busy={isSyncing || undefined}
        aria-disabled={disabled || undefined}
        title={titleText}
        className={`pointer-events-auto relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-transparent transition-colors before:absolute before:-inset-1 before:rounded-full hover:bg-dg-interaction-hover/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dg-primary-600 focus-visible:ring-offset-2 focus-visible:ring-offset-dg-bg-surface sm:h-10 sm:w-10 dark:hover:bg-white/5 dark:focus-visible:ring-offset-slate-900 ${
          disabled ? 'cursor-not-allowed opacity-80' : ''
        }`}
      >
        <RefreshCw
          className={`relative h-4 w-4 ${iconColor} ${isSyncing ? 'animate-spin' : ''}`}
          aria-hidden
        />
      </button>
    </div>
  );
};

/** Full viewport shell: safe-area + tiny margin (see global `.dg-screen-gutter-x-t`). */
export const PageWrapper: React.FC<{
  children: React.ReactNode;
  headerTitle?: string;
  headerSubtitle?: string;
  onBack?: () => void;
  isOnline: boolean;
  isDarkMode: boolean;
  setIsDarkMode: (val: boolean) => void;
  syncStatus?: 'idle' | 'ok' | 'error';
  onSyncClick?: () => void;
  /**
   * Optional override for the header sync icon button click handler.
   * When set, the dead-centre `HeaderSyncButton` invokes this instead
   * of `onSyncClick`. Lets workflow-level shells (e.g. the grading
   * workflow) wire the same Classroom sync handler the in-screen
   * "Sync Classroom" chip used to fire, without disturbing the rest
   * of the app's `onSyncClick` flow.
   */
  onHeaderSyncClick?: () => void;
  /**
   * True while the header sync action is in flight. Spins the icon,
   * sets `aria-busy`, and disables the button so double-taps cannot
   * stack concurrent sync calls.
   */
  isSyncing?: boolean;
  /** Signed-out auth: hide EN/ES toggle (matches production). */
  hideLanguageToggle?: boolean;
  /** Auth screen uses compact header chrome (matches app.donegrading.com). */
  headerAppearance?: 'default' | 'auth';
  /** Grading-only product: opens settings sheet instead of full Options. */
  onOpenSettings?: () => void;
  /** Grading-only: avatar menu (replaces theme/lang in header when set). */
  headerActions?: React.ReactNode;
}> = ({
  children,
  headerTitle = 'DoneGrading',
  headerSubtitle,
  isOnline,
  isDarkMode,
  setIsDarkMode,
  syncStatus = 'idle',
  onSyncClick,
  onHeaderSyncClick,
  isSyncing = false,
  headerAppearance = 'default',
  onOpenSettings,
  headerActions,
}) => {
  const { t } = useTranslation();
  const isAuthChrome = headerAppearance === 'auth';
  const appCtx = React.useContext(AppContext);
  const educatorName = appCtx?.educatorName?.trim() ?? '';
  const resolvedHeaderTitle = !isAuthChrome && educatorName ? educatorName : headerTitle;
  const dateLabel = !isAuthChrome ? (appCtx?.todayLabel?.trim() ?? '') : '';

  /**
   * Mobile-friendly short variant of the long context-derived
   * `todayLabel` (e.g. "Sunday, May 31, 2026" → "Sun, May 31").
   *
   * The header runs out of horizontal real estate on narrow phones
   * (320 / 360 px) when the dead-centre sync icon, the user's full
   * name, and the full date all sit in the same row. Showing a
   * compact weekday + month/day on phones lets the *full* string
   * still render without truncation; the full label re-emerges at
   * the `≥ min-[360px]` breakpoint via the responsive spans below.
   *
   * The source-of-truth `appCtx.todayLabel` is NEVER mutated — we
   * only derive a local short copy via a regex transform so the
   * change stays scoped to the header.
   */
  const dateLabelShort = React.useMemo(() => {
    if (!dateLabel) return '';
    const withoutYear = dateLabel.replace(/,\s*\d{4}\s*$/, '');
    return withoutYear.replace(/^([A-Za-z]+)/u, (match) =>
      match.length > 3 ? match.slice(0, 3) : match
    );
  }, [dateLabel]);

  /**
   * Narrow-width variant of the auth header subtitle (the date). Same
   * weekday-abbreviation + drop-year transform as `dateLabelShort` so
   * the date stays legible when a desktop window is narrowed below the
   * `sm` breakpoint instead of shrinking to a truncated 10px label.
   */
  const headerSubtitleShort = React.useMemo(() => {
    if (!headerSubtitle) return '';
    const withoutYear = headerSubtitle.replace(/,\s*\d{4}\s*$/, '');
    return withoutYear.replace(/^([A-Za-z]+)/u, (match) =>
      match.length > 3 ? match.slice(0, 3) : match
    );
  }, [headerSubtitle]);

  /**
   * Mobile-friendly short variant of the educator's full name
   * (e.g. "Radhouane Boubaya" → "Radhouane B."). Same idea as
   * `dateLabelShort`: keep the full string on `≥ 360px` widths and
   * drop to "First L." only on truly narrow phones so neither the
   * name nor the date ever ellipses out. The source `educatorName`
   * is never mutated.
   */
  const headerTitleShort = React.useMemo(() => {
    const name = resolvedHeaderTitle.trim();
    if (!name) return name;
    const parts = name.split(/\s+/);
    if (parts.length < 2) return name;
    const first = parts[0];
    const lastInitial = parts[parts.length - 1]?.[0] ?? '';
    return lastInitial ? `${first} ${lastInitial}.` : first;
  }, [resolvedHeaderTitle]);

  return (
    <div className="dg-wave-bg flex h-full min-h-0 w-full flex-col overflow-hidden selection:bg-dg-primary-500/25">
      <div className="relative flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden overscroll-x-none dg-screen-gutter-x-t">
        <header
          className={
            isAuthChrome
              ? 'flex h-10 min-h-10 shrink-0 items-center justify-between gap-2 bg-transparent px-3 sm:h-12 sm:min-h-12 sm:gap-3 sm:px-6 laptop:h-14 laptop:min-h-14 laptop:px-8'
              : 'relative flex h-12 min-h-12 shrink-0 items-center justify-between gap-2 bg-transparent px-2 sm:h-14 sm:min-h-14 sm:gap-3 sm:px-5 md:px-6 laptop:h-14 laptop:min-h-14 xl:px-8'
          }
        >
          {/*
            The legacy in-header `ShellMenuButton` (hamburger / "kebab" trigger)
            was replaced by the global, draggable `DraggableSettingsMenu` that
            `App.tsx` mounts once at the shell level. The drawer it opens
            (`MobileShellDrawer`) is unchanged.
          */}
          {isAuthChrome ? (
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              <img
                src="/donegrading-logo.png"
                alt="DoneGrading"
                decoding="async"
                className="h-8 w-8 shrink-0 rounded-full object-cover sm:h-10 sm:w-10 laptop:h-11 laptop:w-11"
              />
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <img
                src="/donegrading-logo.png"
                alt="DoneGrading"
                decoding="async"
                className="h-7 w-7 shrink-0 rounded-full object-cover sm:h-9 sm:w-9"
              />
              <div className="flex min-w-0 flex-1 justify-center">
                <h1 className="dg-fluid-header-label whitespace-nowrap font-semibold leading-tight tracking-tight text-black [text-shadow:_0_1px_2px_rgba(0,0,0,0.25)] dark:text-white dark:[text-shadow:_0_1px_2px_rgba(0,0,0,0.6)]">
                  <span className="min-[360px]:hidden">{headerTitleShort}</span>
                  <span className="hidden min-[360px]:inline">{resolvedHeaderTitle}</span>
                </h1>
              </div>
            </div>
          )}

          {isAuthChrome ? null : (
            <HeaderSyncButton
              isOnline={isOnline}
              syncStatus={syncStatus}
              onClick={onHeaderSyncClick ?? onSyncClick}
              isSyncing={isSyncing}
            />
          )}

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2">
            <div className="flex min-w-0 flex-1 justify-center">
              {(isAuthChrome ? headerSubtitle : dateLabel) ? (
                <span className="dg-fluid-header-label whitespace-nowrap font-semibold leading-tight tracking-tight text-black [text-shadow:_0_1px_2px_rgba(0,0,0,0.25)] dark:text-white dark:[text-shadow:_0_1px_2px_rgba(0,0,0,0.6)]">
                  <span className="min-[360px]:hidden">
                    {isAuthChrome ? headerSubtitleShort : dateLabelShort}
                  </span>
                  <span className="hidden min-[360px]:inline">
                    {isAuthChrome ? headerSubtitle : dateLabel}
                  </span>
                </span>
              ) : null}
            </div>
            {headerActions}
            {!headerActions && onOpenSettings ? (
              <button
                type="button"
                onClick={onOpenSettings}
                aria-label={t('gradingOnly.settings.title')}
                title={t('gradingOnly.settings.title')}
                className="inline-flex h-11 min-h-[44px] w-11 shrink-0 items-center justify-center rounded-dg-control border border-dg-border bg-dg-bg-surface/90 text-dg-fg-primary shadow-dg-card transition-colors hover:bg-dg-interaction-hover dark:border-slate-600 dark:bg-slate-900/90 dark:text-slate-100 dark:hover:bg-slate-800"
              >
                <Settings className="h-5 w-5" aria-hidden />
              </button>
            ) : null}
            {!headerActions ? (
              <ThemeToggle
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                enlargeHitArea={isAuthChrome}
                className={
                  isAuthChrome
                    ? isDarkMode
                      ? 'border border-white/15 bg-white/5 text-white backdrop-blur-xl group-hover:bg-white/10'
                      : 'border border-[#e0e0e0] bg-white text-[#1a1a1a] group-hover:bg-neutral-50'
                    : undefined
                }
              />
            ) : null}
          </div>
        </header>

        <main className="dg-page-main relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden overscroll-x-none">
          {children}
        </main>
      </div>
    </div>
  );
};
